import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Paper, IconButton, Drawer } from "@mui/material";
import {
  ChevronLeft as CloseIcon,
  ChevronRight as OpenIcon,
} from "@mui/icons-material";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import "./GridLayout.scss"; // For smooth transitions
import { useSettings } from "../hook/useSettings"; // Import the custom hook

function GridLayout({ Grid1, Grid2, HideGrid2 = false }) {
  // Manage visibility of Grid 2
  const hideGrid2 = HideGrid2;
  //console.log(hideGrid2);
  const [isGrid2Visible, setIsGrid2Visible] = useState(true); // Manage visibility of Grid 2
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md")); // Detect mobile and tablet screens (below 900px)

  // Use custom hook to fetch settings
  const { settingsData, loading, error } = useSettings();

  // Handle grid layout based on themeGrid value
  const themeGrid = settingsData?.themeGrid;

  const toggleGrid2 = () => {
    setIsGrid2Visible(!isGrid2Visible);
  };

  // If still loading settings, show a loading state
  if (loading) return <div>Loading...</div>;

  // If there is an error, show an error message
  if (error) return <div>Error: {error}</div>;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isSmallScreen && !themeGrid ? "column" : "row", // Change layout to column on small screen when themeGrid is false
        flexWrap: "nowrap", // Prevent wrapping into a new row
        width: "100%", // Ensure the container takes full width
        gap: 2, // Add gap between Grid 1 and Grid 2
      }}
    >
      {/* Grid 1 */}
      <Box
        sx={{
          flexGrow: 1, // Automatically fills remaining space
          minWidth: 0, // Allows shrinking to the minimum width
          position: "relative",
        }}
      >
        <Paper
          sx={{
            height: "100%",
            position: "relative",
            borderStyle: "solid",
            borderRadius: 2,
            borderColor: "rgba(130, 130, 130, 0.1)",
            //padding: isSmallScreen ? 0 : 2, // Apply padding: 0 for small screens
            padding: 2,
            borderWidth: isSmallScreen ? 0 : 1,
            boxShadow: isSmallScreen
              ? "none"
              : "0px 4px 12px rgba(130, 130, 130, 0.15)",
          }}
          elevation={isSmallScreen ? undefined : 1}
        >
          {/* Toggle Button to show/hide Grid 2 (hide on small screen when themeGrid is false) */}
          {!(isSmallScreen && !themeGrid) && (
            <IconButton
              onClick={toggleGrid2}
              sx={{
                position: "absolute",
                top: isSmallScreen ? -15 : 6,
                right: isSmallScreen ? -10 : 8,
                zIndex: 1,
              }}
            >
              {isGrid2Visible ? (
                <CloseIcon fontSize="large" />
              ) : (
                <OpenIcon fontSize="large" />
              )}
            </IconButton>
          )}

          {/* Render custom content passed to Grid1 */}
          {Grid1}
        </Paper>
      </Box>

      {/* Grid 2 for small screens as a Drawer */}
      {isSmallScreen ? (
        themeGrid ? (
          <Drawer
            anchor="right"
            open={isGrid2Visible}
            onClose={toggleGrid2}
            PaperProps={{ sx: { width: "100%", maxWidth: "320px" } }} // Drawer width for small screens
          >
            <Box className="slide-grid-2" sx={{ padding: 2 }}>
              {/* Render custom content passed to Grid2 */}
              {Grid2}
            </Box>
          </Drawer>
        ) : (
          //  hideGrid2 == true, hide Grid2 on small screen when themeGrid is false
          !hideGrid2 && (
            <Box sx={{ width: "100%", padding: 2 }}>
              {/* Render custom content passed to Grid2 */}
              {Grid2}
            </Box>
          )

          // Using Admin Menu - Add empty <Box> element for Grid 2 when themeGrid is false
        )
      ) : (
        // Grid 2 for large screens with smooth transitions
        isGrid2Visible && (
          <Box
            sx={{
              flexBasis: "380px", // Minimum width for Grid 2
              maxWidth: "400px", // Max width for Grid 2
              flexShrink: 0, // Prevent shrinking
              maxHeight: "100vh", // Max height for Grid 2 for scrolling
            }}
            className={`slide-grid ${isGrid2Visible ? "visible" : "hidden"}`} // Add transition classes for smooth sliding
          >
            <Paper
              className="slide-grid-2"
              sx={{
                maxHeight: "100%", // for scrolling
                overflowY: "auto",
                justifyContent: "center",
                padding: 2,
                paddingBottom: 18,
                height: "100%",
                borderStyle: "solid",
                borderColor: "rgba(130, 130, 130, 0.1)",
                borderWidth: 1,
                borderRadius: 2,
                boxShadow: isSmallScreen
                  ? "none"
                  : "0px 4px 12px rgba(130, 130, 130, 0.15)",
              }}
              elevation={isSmallScreen ? undefined : 1}
            >
              {/* Render custom content passed to Grid2 */}
              {Grid2}
            </Paper>
          </Box>
        )
      )}
    </Box>
  );
}

GridLayout.propTypes = {
  /** Grid1 represents the content of the first grid */
  Grid1: PropTypes.node.isRequired,

  /** Grid2 represents the content of the second grid */
  Grid2: PropTypes.node.isRequired,

  /** themeGrid determines whether to show Grid 2 or not */
  HideGrid2: PropTypes.bool,
};

export default GridLayout;
